/**
 * JitterBuffer — small TCP-aware smoothing buffer for the JT/T1078 path.
 *
 * The relay delivers VIDEO_FRAME messages over a TCP-bridged WebSocket, so
 * frames are guaranteed in-order — there is nothing to reorder. The reason
 * this buffer exists is **rate smoothing**: TCP bursts (Nagle + congestion
 * window opening) can deliver 5 frames in a 2 ms window followed by 200 ms
 * of silence. Feeding the decoder this raw cadence produces user-visible
 * stutter even when no frames are lost.
 *
 * Strategy: push each frame with a receive timestamp; pop frames once they
 * have been buffered for at least `targetMs`. The decoder then sees a
 * cadence that is smoothed by the current target latency.
 *
 * The target adapts to observed inter-arrival jitter:
 *   - measure p95 of the inter-push gap over a rolling window
 *   - target = clamp(p95 × 1.5, [minMs, maxMs])
 *
 * Default knobs: 50 ms target, [20, 200] ms range.
 *
 * NOT a reorder buffer. NOT a packet-level retransmit. NOT for WebRTC.
 */

export interface JitterBufferOptions {
  /** Initial target buffer depth in ms. Default 50. */
  initialTargetMs?: number;
  /** Lower clamp. Default 20. */
  minTargetMs?: number;
  /** Upper clamp. Default 200. */
  maxTargetMs?: number;
  /** Rolling window size (frames) for p95 jitter measurement. Default 60. */
  jitterWindowFrames?: number;
}

interface BufferedFrame<T> {
  frame: T;
  /** Wall-clock timestamp when the frame was pushed (performance.now()). */
  recvAt: number;
  /** Media PTS in microseconds (from the VIDEO_FRAME message). */
  ptsUs: number;
  /** Keyframe flag — emitted alongside the frame so the gate can short-circuit. */
  isKeyframe: boolean;
}

/**
 * Floor for the adaptive playback rate when the buffer underfills. 0.92 = play
 * ≤8% slower — barely perceptible — to let the buffer refill from incoming
 * frames instead of draining to a hard stall. 1.0 = normal speed.
 */
const SLOWPLAY_MIN_RATE = 0.92;

export class JitterBuffer<T> {
  private readonly minTargetMs: number;
  private readonly maxTargetMs: number;
  private readonly jitterWindowFrames: number;

  private targetMs: number;
  private queue: BufferedFrame<T>[] = [];
  private lastRecvAt: number | null = null;
  private interArrivals: number[] = [];

  // Paced-release playhead (media PTS, ms) + its wall-clock anchor. The buffer
  // releases frames whose PTS has been reached by the playhead, which advances
  // at `rate` × wall-clock — slowing below 1.0 when the buffer is underfull.
  private playing = false;
  private anchorWallMs = 0;
  private anchorPtsMs = 0;

  constructor(opts: JitterBufferOptions = {}) {
    this.minTargetMs = opts.minTargetMs ?? 20;
    this.maxTargetMs = opts.maxTargetMs ?? 200;
    this.jitterWindowFrames = opts.jitterWindowFrames ?? 60;
    this.targetMs = Math.max(
      this.minTargetMs,
      Math.min(this.maxTargetMs, opts.initialTargetMs ?? 50),
    );
  }

  /**
   * Add a frame to the buffer. Records its receive timestamp and updates the
   * inter-arrival jitter window.
   */
  push(frame: T, ptsUs: number, isKeyframe: boolean): void {
    const now = performance.now();
    if (this.lastRecvAt !== null) {
      const dt = now - this.lastRecvAt;
      this.interArrivals.push(dt);
      if (this.interArrivals.length > this.jitterWindowFrames) {
        this.interArrivals.shift();
      }
    }
    this.lastRecvAt = now;

    // Push every frame in arrival order. The relay delivers frames in decode
    // order over TCP, so arrival order IS the correct order — we must NOT
    // reorder. A previous "keyframes bypass smoothing" optimization unshifted
    // keyframes to the FRONT of the queue, jumping them ahead of the previous
    // GOP's trailing delta frames still buffered. That reordered PTS — keyframe
    // N.0 emitted before the queued N-0.1 delta — which the WebCodecs decoder
    // rejects ("Decoding error") and the FrameQualityGate misreads as a
    // backward-PTS anchor loss, producing perpetual "recovering" churn.
    // Keyframes still drain promptly: they carry the smallest backlog wait and
    // the gate re-anchors on them as soon as they reach the decoder in order.
    this.queue.push({ frame, recvAt: now, ptsUs, isKeyframe });
  }

  /**
   * Release frames due for presentation. Two phases:
   *
   * 1. Buffering — hold until the oldest frame has aged `targetMs`, building the
   *    initial cushion. Then start the playhead anchored at that frame's PTS.
   * 2. Paced playback — advance a PTS playhead at `rate` × wall-clock and
   *    release every frame whose PTS the playhead has passed. `rate` drops
   *    toward SLOWPLAY_MIN_RATE as the buffered media ahead of the playhead
   *    falls below `targetMs`, so the buffer refills (slightly slow-motion)
   *    instead of draining to a hard stall. At/above target, rate = 1.0.
   *
   * Frames stay in PTS order (TCP-ordered); no reordering.
   */
  drain(): BufferedFrame<T>[] {
    const now = performance.now();
    if (this.queue.length === 0) return [];

    if (!this.playing) {
      const front = this.queue[0];
      if (now - front.recvAt < this.targetMs) return [];
      this.playing = true;
      this.anchorWallMs = now;
      this.anchorPtsMs = front.ptsUs / 1000;
    }

    const newestPtsMs = this.queue[this.queue.length - 1].ptsUs / 1000;
    const occupancyMs = newestPtsMs - this.anchorPtsMs; // media buffered ahead of playhead
    const fill = this.targetMs > 0 ? occupancyMs / this.targetMs : 1;
    const rate = Math.max(
      SLOWPLAY_MIN_RATE,
      Math.min(1, SLOWPLAY_MIN_RATE + (1 - SLOWPLAY_MIN_RATE) * fill),
    );

    const playheadPtsMs = this.anchorPtsMs + (now - this.anchorWallMs) * rate;
    this.anchorPtsMs = playheadPtsMs;
    this.anchorWallMs = now;

    const out: BufferedFrame<T>[] = [];
    while (this.queue.length > 0 && this.queue[0].ptsUs / 1000 <= playheadPtsMs) {
      out.push(this.queue[0]);
      this.queue.shift();
    }
    return out;
  }

  /**
   * Update the adaptive target based on the current jitter window. Should be
   * called periodically (e.g. every 1 s) — calling it on every push would
   * dominate CPU during burst arrivals.
   */
  adapt(): void {
    if (this.interArrivals.length < 10) return;
    const sorted = this.interArrivals.slice().sort((a, b) => a - b);
    const p95 = sorted[Math.floor(sorted.length * 0.95)];
    const next = p95 * 1.5;
    this.targetMs = Math.max(this.minTargetMs, Math.min(this.maxTargetMs, next));
  }

  /** Wipe queue + jitter window. Call on detach or on VIDEO_INIT (resolution change). */
  reset(): void {
    this.queue = [];
    this.lastRecvAt = null;
    this.interArrivals = [];
    this.playing = false;
    this.anchorWallMs = 0;
    this.anchorPtsMs = 0;
  }

  /** Current adaptive target in ms — for telemetry/debug. */
  get currentTargetMs(): number {
    return this.targetMs;
  }

  /** Number of frames waiting to drain — for telemetry/debug. */
  get pendingFrames(): number {
    return this.queue.length;
  }
}
